import type { ProjectOption } from '@atlassian/jira-issue-field-project/src/common/types.tsx';
import { useProjectField } from '@atlassian/jira-issue-field-project/src/services/index.tsx';
import { PROJECT_PICKER_CF_TYPE } from '@atlassian/jira-platform-field-config';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasRichFormat,
} from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	PasteHandler,
	PasteMetadata,
} from '../../../types';
import type { TransformedProjectOption, TransformedProjectField } from '../types';

const isProjectOption = (fieldValue: unknown): fieldValue is ProjectOption =>
	fieldValue != null &&
	typeof fieldValue === 'object' &&
	'name' in fieldValue &&
	'avatarUrls' in fieldValue &&
	'key' in fieldValue;

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): TransformedProjectOption | null => {
	if (
		!assertClipboardHasRichFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, [PROJECT_PICKER_CF_TYPE])
	) {
		return null;
	}

	if (!isProjectOption(metadata.fieldValue)) {
		return null;
	}

	const { avatarUrls, key, name } = metadata.fieldValue;

	return {
		avatarUrls,
		key,
		name,
	};
};

function assertProjectField(
	field: ListIssueFieldTransformed,
): asserts field is TransformedProjectField {
	if (!field) {
		throw new Error('Field is required');
	}

	if (field.type !== PROJECT_PICKER_CF_TYPE) {
		throw new Error('Field type is not supported');
	}
}

const useProjectClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	assertProjectField(field);

	const [{ value }, { saveValue }] = useProjectField({
		issueKey: issue.key,
		fieldKey: field?.fieldId,
		initialValue: field?.value ?? null,
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: value?.name ?? '',
		currentValue: value,
		getPastedValue,
		saveValue,
	});
};

const ProjectClipboardHelper = createClipboardHelperComponent(useProjectClipboardHandler);

export const projectClipboardHandler = {
	[PROJECT_PICKER_CF_TYPE]: ProjectClipboardHelper,
};
